"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime, cn } from "@/lib/utils";
import { zoradTimyPodlaIIHF } from "@/lib/utils/iihf-ranking";
import { MapPin, Trophy, Clock, Users, Target, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { Flag } from "@/components/shared/flags";
import Link from "next/link";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { Skupina, Krajina, KrajinaSkupina, Zapas } from "@/types/database";

interface ApiResponse {
  skupina: Skupina;
  krajinaskupina: (KrajinaSkupina & { krajina: Krajina })[];
  zapasy: (Zapas & { domaci: Krajina; hosti: Krajina })[];
}

const HEADER_SECTION_CLASSES = cn(
  "bg-muted/40 px-4 py-3 border-b border-muted/20 flex flex-col sm:flex-row",
  "sm:justify-between sm:items-center text-[10px] font-black uppercase",
  "tracking-[0.15em] text-muted-foreground/80 gap-2 sm:gap-4",
);
const SCORE_CONTAINER_CLASSES = cn(
  "flex flex-col items-center gap-3 px-4 sm:px-6 md:px-8",
  "justify-center min-w-[120px] sm:min-w-[140px]",
);
const TEAM_SECTION_CLASSES = cn(
  "flex-1 text-center space-y-2 min-w-0 group/team",
);
const TEAM_LAYOUT_CLASSES = cn("flex items-center justify-center gap-3");
const FLAG_SIZE = 32;

export function SkupinaDetailPage({ data }: { data: ApiResponse }) {
  // Priame použitie dát z props
  const skupina = data.skupina;
  // Správne zoradenie tímov podľa IIHF pravidiel
  const krajinaskupina = zoradTimyPodlaIIHF(data.krajinaskupina, data.zapasy);
  const zapasy = data.zapasy;

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header Section */}
      <section className="bg-muted/10 border-b border-muted/20 py-8 md:py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-6"
            >
              <Button asChild variant="ghost" className="mb-4">
                <Link href="/skupiny" className="flex items-center gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Späť na skupiny
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center md:text-left"
            >
              <Badge
                variant="outline"
                className="mb-3 bg-muted/50 text-muted-foreground border-muted/20 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-widest"
              >
                ZOH 2026 • Skupina {skupina.slug?.toUpperCase()}
              </Badge>
              <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-4 uppercase">
                {skupina.nazov}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground/80 font-medium leading-relaxed max-w-2xl">
                Základná skupina hokejového turnaja ZOH 2026 v Miláne.{" "}
                {krajinaskupina.length} tímov bojuje o postup do play-off.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-6xl mx-auto space-y-12">
          {/* Tabuľka tímov - hneď na vrchu */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-primary/10 rounded-2xl text-primary">
                <Users className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-black tracking-tighter uppercase">
                  Tímy v skupine
                </h2>
                <p className="text-muted-foreground/80 font-medium mt-1">
                  Aktuálna tabuľka a poradia
                </p>
              </div>
            </div>

            <Card className="border-muted/20 bg-transparent shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/5 border-b border-muted/10">
                      <TableHead className="w-16 text-center font-bold">
                        #
                      </TableHead>
                      <TableHead>Krajina</TableHead>
                      <TableHead className="text-center">Z</TableHead>
                      <TableHead className="text-center">V</TableHead>
                      <TableHead className="text-center">R</TableHead>
                      <TableHead className="text-center">P</TableHead>
                      <TableHead className="text-center">Skóre</TableHead>
                      <TableHead className="text-center">B</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {krajinaskupina.map((krajinaSkupina, index) => (
                      <TableRow
                        key={krajinaSkupina.id}
                        className="border-b border-muted/10 hover:bg-muted/5 transition-colors"
                      >
                        <TableCell className="text-center font-black text-muted-foreground">
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-1 bg-background border border-muted/20 shadow-sm rounded-lg overflow-hidden">
                              <Flag
                                kod={krajinaSkupina.krajina.kod}
                                size={24}
                              />
                            </div>
                            <div className="flex flex-col">
                              <span className="font-bold text-foreground uppercase tracking-tight">
                                {krajinaSkupina.krajina.kod}
                              </span>
                              <span className="text-xs text-muted-foreground/60">
                                {krajinaSkupina.krajina.nazov}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {krajinaSkupina.vyhry +
                            krajinaSkupina.remizy_sn +
                            krajinaSkupina.prehry}
                        </TableCell>
                        <TableCell className="text-center font-medium text-status-success">
                          {krajinaSkupina.vyhry}
                        </TableCell>
                        <TableCell className="text-center font-medium text-status-warning">
                          {krajinaSkupina.remizy_sn}
                        </TableCell>
                        <TableCell className="text-center font-medium text-status-error">
                          {krajinaSkupina.prehry}
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {krajinaSkupina.skore_za}:{krajinaSkupina.skore_priem}
                        </TableCell>
                        <TableCell className="text-center font-black text-primary">
                          {krajinaSkupina.body}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </motion.section>

          {/* Zápasy skupiny */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-primary/10 rounded-2xl text-primary">
                <Trophy className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase">
                  Zápasy skupiny
                </h2>
                <p className="text-muted-foreground/80 font-medium mt-1">
                  Program a výsledky zápasov
                </p>
              </div>
            </div>

            <div className="grid gap-6">
              {zapasy.map((zapas, mapIdx) => (
                <motion.div
                  key={zapas.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: mapIdx * 0.1 }}
                  className="group"
                >
                  <Link href={`/zapas/${zapas.id}`} className="block">
                    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-muted/20 bg-background/50 backdrop-blur-sm cursor-pointer">
                      <div className={HEADER_SECTION_CLASSES}>
                        <div className="flex items-center gap-2 text-primary font-black">
                          <Trophy className="h-3.5 w-3.5" />
                          {zapas.faza}
                        </div>
                        <div className="flex items-center gap-2 font-bold">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDateTime(zapas.datum_cas)}
                        </div>
                      </div>
                      <CardContent className="p-6 md:p-8">
                        <div className="flex items-center justify-between gap-4">
                          <div className={TEAM_SECTION_CLASSES}>
                            <Link
                              href={`/krajina/${zapas.domaci.kod.toLowerCase()}`}
                              className="block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className={TEAM_LAYOUT_CLASSES}>
                                <div className="text-3xl md:text-4xl font-black tracking-tighter group-hover/team:text-primary transition-colors uppercase">
                                  {zapas.domaci.kod}
                                </div>
                                <Flag kod={zapas.domaci.kod} size={FLAG_SIZE} />
                              </div>
                              <div className="text-[10px] md:text-xs font-black text-muted-foreground/60 uppercase tracking-[0.2em] truncate max-w-[120px] mx-auto hover:text-primary transition-colors">
                                {zapas.domaci.nazov}
                              </div>
                            </Link>
                          </div>

                          <div className={SCORE_CONTAINER_CLASSES}>
                            {zapas.stav === "ukonceny" ? (
                              <div className="text-4xl md:text-5xl font-black tracking-tighter tabular-nums drop-shadow-sm">
                                {zapas.skore_domaci} : {zapas.skore_hosti}
                              </div>
                            ) : (
                              <div className="text-2xl font-black text-muted-foreground/30 italic tracking-widest">
                                VS
                              </div>
                            )}
                            <Badge
                              variant={
                                zapas.stav === "ukonceny"
                                  ? "secondary"
                                  : "outline"
                              }
                              className={cn(
                                "rounded-full px-4 py-1 text-[10px] font-black uppercase tracking-widest",
                                zapas.stav === "ukonceny"
                                  ? "bg-status-success/10 text-status-success border-status-success/20"
                                  : "bg-muted/30 text-muted-foreground border-muted/20",
                              )}
                            >
                              {zapas.stav}
                            </Badge>
                          </div>

                          <div className={TEAM_SECTION_CLASSES}>
                            <Link
                              href={`/krajina/${zapas.hosti.kod.toLowerCase()}`}
                              className="block"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className={TEAM_LAYOUT_CLASSES}>
                                <Flag kod={zapas.hosti.kod} size={FLAG_SIZE} />
                                <div className="text-3xl md:text-4xl font-black tracking-tighter group-hover/team:text-primary transition-colors uppercase">
                                  {zapas.hosti.kod}
                                </div>
                              </div>
                              <div className="text-[10px] md:text-xs font-black text-muted-foreground/60 uppercase tracking-[0.2em] truncate max-w-[120px] mx-auto hover:text-primary transition-colors">
                                {zapas.hosti.nazov}
                              </div>
                            </Link>
                          </div>
                        </div>

                        {zapas.miesto && (
                          <div className="mt-8 pt-6 border-t border-muted/10 flex justify-center items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                            <MapPin className="h-3.5 w-3.5 text-primary/60" />
                            <span className="truncate">{zapas.miesto}</span>
                          </div>
                        )}
                        {zapas.poznamka && (
                          <div className="mt-4 flex justify-center items-center gap-2 text-[10px] font-medium text-muted-foreground/80 italic">
                            <span className="truncate">{zapas.poznamka}</span>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Formát skupiny a pravidlá */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-3 bg-primary/10 rounded-2xl text-primary">
                <Target className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tighter uppercase">
                  Formát skupiny
                </h2>
                <p className="text-muted-foreground/80 font-medium mt-1">
                  Pravidlá postupu a hodnotenie
                </p>
              </div>
            </div>

            <Accordion type="single" collapsible className="w-full">
              <AccordionItem
                value="format"
                className="border-muted/20 bg-transparent rounded-2xl px-4 sm:px-6 shadow-sm mb-4"
              >
                <AccordionTrigger className="hover:no-underline py-8">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-muted rounded-xl text-primary">
                      <Trophy className="h-6 w-6" />
                    </div>
                    <span className="text-xl font-black uppercase tracking-tight">
                      Postupový kľúč do play-off
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground/80 leading-relaxed pb-8">
                  <div className="space-y-6 pt-4">
                    <p className="font-medium text-lg">
                      Systém turnaja je navrhnutý tak, aby odmenil najlepšie
                      tímy zo základnej časti:
                    </p>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div className="p-6 bg-muted/20 rounded-2xl border border-muted/20 space-y-4">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-status-success text-white font-black uppercase tracking-widest text-[10px] px-3">
                            Priamy postup
                          </Badge>
                          <h4 className="font-black text-foreground uppercase tracking-tight">
                            Štvrťfinále
                          </h4>
                        </div>
                        <ul className="space-y-2 font-medium">
                          <li className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-status-success" />
                            Víťazi skupín A, B, C
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-status-success" />
                            Najlepší tím z 2. miest
                          </li>
                        </ul>
                      </div>
                      <div className="p-6 bg-muted/20 rounded-2xl border border-muted/20 space-y-4">
                        <div className="flex items-center gap-3">
                          <Badge
                            variant="secondary"
                            className="font-black uppercase tracking-widest text-[10px] px-3"
                          >
                            Kvalifikácia
                          </Badge>
                          <h4 className="font-black text-foreground uppercase tracking-tight">
                            Osemfinále
                          </h4>
                        </div>
                        <p className="font-medium text-sm leading-relaxed">
                          Zostávajúcich 8 tímov (zvyšné 2. miesta a všetky 3. a
                          4. miesta) bojuje o zvyšné 4 miestenky v priamom
                          súboji.
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="scoring"
                className="border-muted/20 bg-transparent rounded-2xl px-4 sm:px-6 shadow-sm mb-4"
              >
                <AccordionTrigger className="hover:no-underline py-8">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-muted rounded-xl text-primary">
                      <Target className="h-6 w-6" />
                    </div>
                    <span className="text-xl font-black uppercase tracking-tight">
                      Hodnotenie zápasov
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground/80 leading-relaxed pb-8">
                  <div className="pt-4 space-y-6">
                    <p className="font-medium text-lg">
                      Základné pravidlá prideľovania bodov v skupine:
                    </p>
                    <div className="grid gap-4">
                      {[
                        {
                          body: "3",
                          popis: "Výhra v riadnom čase",
                          typ: "success",
                        },
                        {
                          body: "2",
                          popis: "Výhra po predĺžení",
                          typ: "warning",
                        },
                        {
                          body: "1",
                          popis: "Prehra po predĺžení",
                          typ: "warning",
                        },
                        {
                          body: "0",
                          popis: "Prehra v riadnom čase",
                          typ: "error",
                        },
                      ].map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-4 p-4 bg-muted/10 rounded-lg border border-muted/10"
                        >
                          <div
                            className={cn(
                              "flex items-center justify-center w-12 h-12 rounded-full text-black font-black text-lg",
                              item.typ === "success" && "bg-status-success",
                              item.typ === "warning" && "bg-status-warning",
                              item.typ === "error" && "bg-status-error",
                            )}
                          >
                            {item.body}
                          </div>
                          <span className="font-medium">{item.popis}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                value="criteria"
                className="border-muted/20 bg-transparent rounded-2xl px-4 sm:px-6 shadow-sm"
              >
                <AccordionTrigger className="hover:no-underline py-8">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-muted rounded-xl text-primary">
                      <Target className="h-6 w-6" />
                    </div>
                    <span className="text-xl font-black uppercase tracking-tight">
                      Kritériá pri rovnosti bodov
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground/80 leading-relaxed pb-8">
                  <div className="pt-4 space-y-6">
                    <p className="font-medium text-lg">
                      Ak majú dva alebo viac tímov v skupine rovnaký počet
                      bodov, rozhodujú tieto kritériá v poradí:
                    </p>
                    <ol className="grid gap-3 font-bold uppercase text-[11px] tracking-widest text-muted-foreground">
                      {[
                        "Vyšší počet bodov zo vzájomných zápasov",
                        "Lepší gólový rozdiel zo vzájomných zápasov",
                        "Vyšší počet strelených gólov vo vzájomných zápasoch",
                        "Výsledok proti najbližšiemu najlepšiemu tímu mimo skupiny",
                        "Postavenie v rebríčku IIHF 2025",
                      ].map((text, idx) => (
                        <li
                          key={idx}
                          className="flex items-center gap-4 bg-muted/10 p-3 rounded-lg border border-muted/10"
                        >
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-black">
                            {idx + 1}
                          </span>
                          <span>{text}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.section>
        </div>
      </main>
    </div>
  );
}
